import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import HourglassEmptyIcon from "@mui/icons-material/HourglassEmpty";
import OpenInNewIcon from "@mui/icons-material/OpenInNew";
import PersonAddAlt1Icon from "@mui/icons-material/PersonAddAlt1";
import { Box, Button, Chip, Link, Typography } from "@mui/material";
import { formatQuotedPrice, formatSentLabel, VendorQuoteRow } from "./vendorQuoteUtils";

type Props = {
  vendors: VendorQuoteRow[];
  canAssign?: boolean;
  onAssignVendor?: (vendorId: string) => void;
  requestedDate: string;
};

function ResponseStatusChip({ status }: { status: NonNullable<VendorQuoteRow["responseStatus"]> }) {
  if (status === "received") {
    return (
      <Chip
        icon={<CheckCircleIcon sx={{ fontSize: "16px !important" }} />}
        label="Quote received"
        size="small"
        sx={{
          height: 24,
          fontWeight: 600,
          fontSize: 12,
          backgroundColor: "#E8F5E9",
          color: "#2E7D32",
          "& .MuiChip-icon": { color: "#2E7D32" },
        }}
      />
    );
  }

  if (status === "declined") {
    return (
      <Chip
        label="Declined"
        size="small"
        sx={{
          height: 24,
          fontWeight: 600,
          fontSize: 12,
          backgroundColor: "#FFEBEE",
          color: "#C62828",
        }}
      />
    );
  }

  return (
    <Chip
      icon={<HourglassEmptyIcon sx={{ fontSize: "16px !important" }} />}
      label="Awaiting response"
      size="small"
      sx={{
        height: 24,
        fontWeight: 600,
        fontSize: 12,
        backgroundColor: "#FFF8E1",
        color: "#F57F17",
        "& .MuiChip-icon": { color: "#F57F17" },
      }}
    />
  );
}

export default function VendorQuoteResponsesSection({
  vendors,
  canAssign = false,
  onAssignVendor,
  requestedDate,
}: Props) {
  if (vendors.length === 0) {
    return null;
  }

  return (
    <Box sx={{ mb: 3 }}>
      <Typography
        variant="caption"
        color="text.secondary"
        sx={{ display: "block", mb: 1.5, fontWeight: 700, letterSpacing: 0.8 }}
      >
        RESPONSES
      </Typography>

      <Box sx={{ display: "flex", flexDirection: "column", gap: 1.5 }}>
        {vendors.map((vendor) => (
          <Box
            key={vendor.id}
            sx={{
              p: 2,
              borderRadius: "8px",
              border: "1px solid #ECECEC",
              backgroundColor: "background.paper",
            }}
          >
            <Box sx={{ display: "flex", alignItems: "flex-start", gap: 1.5 }}>
              <Box
                sx={{
                  width: 36,
                  height: 36,
                  borderRadius: "8px",
                  backgroundColor: vendor.color,
                  color: "#fff",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  fontWeight: 700,
                  fontSize: 14,
                  flexShrink: 0,
                }}
              >
                {vendor.initial}
              </Box>

              <Box sx={{ flex: 1, minWidth: 0 }}>
                <Box sx={{ display: "flex", alignItems: "center", gap: 1, flexWrap: "wrap", mb: 0.5 }}>
                  <Typography fontWeight={700} sx={{ fontSize: 15 }}>
                    {vendor.name}
                  </Typography>
                  {vendor.responseStatus && <ResponseStatusChip status={vendor.responseStatus} />}
                  {vendor.proposedDate && vendor.proposedDate != requestedDate && (
                    <Chip color="warning" label={`Proposed: ${vendor.proposedDate}`} size="small" />
                  )}
                </Box>
                <Typography variant="body2" color="text.secondary" sx={{ fontSize: 12 }}>
                  {vendor.vendorType}
                  {vendor.sentAt ? ` · Sent ${formatSentLabel(vendor.sentAt)}` : ""}
                </Typography>
                {vendor.portalUrl && (
                  <Link
                    href={vendor.portalUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    sx={{ fontSize: 13, display: "inline-flex", alignItems: "center", gap: 0.5, mt: 0.5 }}
                  >
                    Hermes portal
                    <OpenInNewIcon sx={{ fontSize: 14 }} />
                  </Link>
                )}
              </Box>

              <Typography fontWeight={700} sx={{ fontSize: 15, flexShrink: 0, color: "text.primary" }}>
                {vendor.hasQuote && vendor.quoteAmountInCents != null
                  ? formatQuotedPrice(vendor.quoteAmountInCents)
                  : "—"}
              </Typography>
            </Box>

            {vendor.responseStatus === "received" && canAssign && onAssignVendor && (
              <Box sx={{ display: "flex", gap: 1, mt: 2 }}>
                <Button
                  variant="contained"
                  startIcon={<PersonAddAlt1Icon />}
                  onClick={() => onAssignVendor(vendor.id)}
                  sx={{ textTransform: "uppercase", fontWeight: 700, fontSize: 12 }}
                >
                  Assign Vendor
                </Button>
              </Box>
            )}
          </Box>
        ))}
      </Box>
    </Box>
  );
}
